import { auth, db } from './config';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import {
  doc, setDoc, getDoc, collection,
  query, where, getDocs, serverTimestamp,
  Timestamp
} from 'firebase/firestore';
 
// Generate unique serial number
const generateSerial = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `MIA-${new Date().getFullYear()}-${result}`;
};
 
// Returns days remaining in trial (0 if expired, null if no trial)
export const getTrialDaysLeft = (trialEnds) => {
  if (!trialEnds) return null;
  const endsMs = trialEnds?.toMillis ? trialEnds.toMillis() : trialEnds;
  const msLeft = endsMs - Date.now();
  if (msLeft <= 0) return 0;
  return Math.ceil(msLeft / (1000 * 60 * 60 * 24));
};
 
// Returns true if the trial is still active
export const isTrialActive = (trialEnds) => {
  const days = getTrialDaysLeft(trialEnds);
  return days !== null && days > 0;
};
 
// Sign up master account — sends verification email before granting access
export const signUpMaster = async (email, password, companyName) => {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  const uid = cred.user.uid;
  const serialNumber = generateSerial();
 
  // Send verification email immediately
  await sendEmailVerification(cred.user);
 
  // 30-day trial from signup
  const trialEnds = Timestamp.fromMillis(Date.now() + 30 * 24 * 60 * 60 * 1000);
 
  // Create org
  const orgRef = doc(collection(db, 'organizations'));
  await setDoc(orgRef, {
    companyName,
    masterUid: uid,
    createdAt: serverTimestamp(),
    userLimit: 5,
    serialNumber,
    active: true,
    plan: 'trial',
    planType: 'cloud',
    trialEnds,
  });
 
  // Create user record
  await setDoc(doc(db, 'users', uid), {
    email,
    role: 'master',
    orgId: orgRef.id,
    companyName,
    createdAt: serverTimestamp()
  });
 
  return { uid, orgId: orgRef.id, serialNumber };
};
 
// Sign in
export const signIn = async (email, password) => {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
  return { uid: cred.user.uid, ...userDoc.data() };
};
 
// Resend verification email
export const resendVerification = async () => {
  if (auth.currentUser && !auth.currentUser.emailVerified) {
    await sendEmailVerification(auth.currentUser);
  }
};
 
// Reload Firebase user to pick up emailVerified after they click the link
export const reloadCurrentUser = async () => {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser.emailVerified;
  }
  return false;
};
 
// Sign out
export const logOut = () => signOut(auth);
 
// Get current user data from Firestore
export const getUserData = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
};
 
// Get org data by orgId
export const getOrgData = async (orgId) => {
  const snap = await getDoc(doc(db, 'organizations', orgId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
 
// Invite a team user (master only)
export const inviteUser = async (email, password, orgId, companyName) => {
  const q = query(collection(db, 'users'), where('orgId', '==', orgId));
  const snap = await getDocs(q);
  if (snap.size >= 6) throw new Error('User limit reached. Max 5 users per account.');
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(cred.user);
  await setDoc(doc(db, 'users', cred.user.uid), {
    email,
    role: 'user',
    orgId,
    companyName,
    createdAt: serverTimestamp()
  });
  return cred.user.uid;
};
 
// Auth state listener
export const onAuth = (callback) => onAuthStateChanged(auth, callback);